import React from 'react';
import WikiIcon from '../../shared/WikiIcon';
import { BRAIN_DETAIL, BRAIN_HOW_TO_READ } from './brainCopy';
import {
    brainAddChildLabel, brainBranchOutLabel, brainCanActivate, brainCanSibling, brainFiresLabel, brainNodePath,
    brainOriginChip, brainPassChip, brainRoleChipLabel, brainStatusChip, isCheckNode, isGroupNode,
    isProposalNode,
} from './brainTreeModel';

const ROLE_TONES = { Heart: 'heart', Brain: 'brain', Driver: 'driver' };

function Chip({ tone, children, testId }) {
    return <span className={`wk-brain-chip wk-brain-chip--${tone}`} data-testid={testId}>{children}</span>;
}

function ActionButton({ variant = 'secondary', icon, onClick, disabled, children, testId }) {
    return (
        <button
            type="button"
            className={`wk-brain-btn wk-brain-btn--${variant}`}
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
        >
            <WikiIcon name={icon} size={13} />
            {children}
        </button>
    );
}

/** Nothing selected (prototype `brNoSel`). */
function HowToRead({ onOpenReview }) {
    const H = BRAIN_HOW_TO_READ;
    return (
        <div data-testid="brain-detail-empty">
            <h3 className="wk-t-h">{H.title}</h3>
            <p className="wk-t-dense wk-brain-detail-intro">{H.intro}</p>
            <div className="wk-brain-howto">
                <div className="wk-brain-howto-row">
                    <WikiIcon name="sparkle" size={14} className="wk-brain-ic-learned wk-brain-howto-ic" />
                    <span><b>Learned</b>{H.learned}</span>
                </div>
                <div className="wk-brain-howto-row">
                    <span className="wk-brain-proposed-swatch wk-brain-howto-swatch" aria-hidden="true" />
                    <span><b>Proposed</b>{H.proposed}</span>
                </div>
                <div className="wk-brain-howto-row">
                    <WikiIcon name="alert-triangle" size={14} className="wk-brain-ic-weak wk-brain-howto-ic" />
                    <span><b>Weak</b>{H.weak}</span>
                </div>
            </div>
            <div className="wk-brain-howto-row wk-brain-howto-grow">
                <WikiIcon name="plus" size={14} className="wk-brain-ic-teal wk-brain-howto-ic" />
                <span><b>{H.growLead}</b>{H.growA}<b>{H.growB}</b>{H.growC}</span>
            </div>
            <button type="button" className="wk-brain-btn wk-brain-btn--dark wk-brain-open-queue" onClick={onOpenReview}>
                {H.openQueue}
                <WikiIcon name="chevron-right" size={14} />
            </button>
        </div>
    );
}

/**
 * The detail panel (E1 §1.3.7–§1.3.8). Blocks 13–15 (lifecycle, BUILD THE
 * TREE, Flag / Turn off) are manage-only; an Editor sees 1–12.
 */
export default function BrainNodeDetail({
    node, byId, canManage, testing = false, busy = false, modePaused = false,
    onClose, onOpenReview, onAddChild, onBranchOut, onRunTest, onSwitchOn, onTurnOff, onFlag, onDelete,
}) {
    if (!node) {
        return (
            <aside className="wk-card wk-brain-detail" aria-label="How to read the tree">
                <HowToRead onOpenReview={onOpenReview} />
            </aside>
        );
    }

    const D = BRAIN_DETAIL;
    const check = isCheckNode(node);
    const group = isGroupNode(node);
    const proposalNode = isProposalNode(node);
    const status = testing ? 'testing' : node.status;
    const statusChip = brainStatusChip(node, { testing });
    const originChip = brainOriginChip(node);
    const passChip = brainPassChip(node.stats?.pass);
    const options = Array.isArray(node.options) ? node.options : [];
    const replay = node.replay || null;
    const weak = Boolean(node.stats?.weak);
    const canChild = canManage && Boolean(node.can_child) && !proposalNode;
    const canSibling = canManage && brainCanSibling(node);
    const showLifecycle = canManage && Boolean(node.editable) && !testing;
    const showFlagRow = canManage && check && !proposalNode && (status === 'active' || status === 'off');
    // Never offer Switch on for a paused node, or a tenant node while the Brain runs the base tree only.
    const canActivate = brainCanActivate(node, { modePaused });

    return (
        <aside className="wk-card wk-brain-detail" aria-label={node.label} data-testid="brain-detail">
            <div className="wk-brain-detail-head">
                <div className="wk-t-meta wk-brain-detail-path" data-testid="brain-detail-path">
                    {brainNodePath(node, byId)}
                </div>
                <button
                    type="button"
                    className="wk-brain-iconbtn"
                    onClick={onClose}
                    title="Close"
                    aria-label="Close"
                    data-testid="brain-detail-close"
                >
                    <WikiIcon name="x" size={14} />
                </button>
            </div>
            <h3 className="wk-t-h wk-brain-detail-title" data-testid="brain-detail-title">
                {node.question || node.label}
            </h3>
            <div className="wk-brain-chips">
                {group && node.role && (
                    <span className={`wk-brain-rolechip wk-brain-rolechip--${ROLE_TONES[node.role] || 'other'}`}>
                        {brainRoleChipLabel(node.role)}
                    </span>
                )}
                <Chip tone={statusChip.tone} testId="brain-detail-status">{statusChip.label}</Chip>
                <Chip tone={originChip.tone} testId="brain-detail-origin">{originChip.label}</Chip>
                <Chip tone="grey" testId="brain-detail-type">{node.type_label || 'Group'}</Chip>
                <Chip tone="grey" testId="brain-detail-version">{node.version_label || 'v1'}</Chip>
            </div>

            {node.note && (
                <div className="wk-brain-box wk-brain-box--note" data-testid="brain-detail-note">
                    <WikiIcon name="sparkle" size={13} />
                    <span>{node.note}</span>
                </div>
            )}
            {weak && (
                <div className="wk-brain-box wk-brain-box--weak" data-testid="brain-detail-weak">
                    <WikiIcon name="alert-triangle" size={13} />
                    <span>{node.stats?.weak_detail || D.weakFallback}</span>
                </div>
            )}

            <div className="wk-t-label wk-brain-label">{D.effectLabel}</div>
            <p className="wk-t-dense wk-brain-detail-effect" data-testid="brain-detail-effect">
                {node.effect || D.effectFallback}
            </p>

            {check && (
                <div className="wk-brain-tiles">
                    <div className="wk-brain-tile-cell">
                        <div className="wk-t-meta">{D.replayCaption}</div>
                        <div className="wk-brain-tile-value">
                            <Chip tone={passChip.tone} testId="brain-detail-pass">{passChip.label}</Chip>
                        </div>
                    </div>
                    <div className="wk-brain-tile-cell">
                        <div className="wk-t-meta">{D.usageCaption}</div>
                        <div className="wk-brain-tile-usage" data-testid="brain-detail-usage">
                            {brainFiresLabel(node.stats?.fires_30d)}
                        </div>
                    </div>
                </div>
            )}

            {options.length > 0 && (
                <>
                    <div className="wk-t-label wk-brain-label">{D.optionsLabel}</div>
                    {node.dynamic === 'category' && <p className="wk-brain-dynamic-note">{D.dynamicNote}</p>}
                    <ul className="wk-brain-options" data-testid="brain-detail-options">
                        {options.map((option, index) => (
                            <li key={option.id || `${option.label}-${index}`} className="wk-brain-option">
                                <div className="wk-brain-option-label">{option.label}</div>
                                {option.description && <div className="wk-t-meta wk-brain-option-what">{option.description}</div>}
                                {option.not_for && option.not_for !== '-' && (
                                    <div className="wk-brain-option-not">{`Not for: ${option.not_for}`}</div>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {node.proposal && (
                <button
                    type="button"
                    className="wk-brain-box wk-brain-box--proposal"
                    onClick={onOpenReview}
                    data-testid="brain-detail-proposal"
                >
                    <WikiIcon name="git-merge" size={15} />
                    <span className="wk-brain-box-grow"><b>{D.waiting}</b>{` ${node.proposal.title || ''}`}</span>
                    <WikiIcon name="chevron-right" size={14} />
                </button>
            )}
            {node.when && (
                <div className="wk-brain-box wk-brain-box--when" data-testid="brain-detail-when">
                    <WikiIcon name="git-merge" size={13} />
                    <span>{`Only asked when the parent's answer is "${node.when}"`}</span>
                </div>
            )}
            {status === 'testing' && (
                <div className="wk-brain-box wk-brain-box--testing" data-testid="brain-detail-testing">
                    <WikiIcon name="refresh-cw" size={14} className="wk-brain-spin" />
                    <span>{D.testing}</span>
                </div>
            )}
            {status === 'tested' && (
                <div className="wk-brain-box wk-brain-box--tested wk-brain-box--stack" data-testid="brain-detail-tested">
                    <div className="wk-brain-box-title">{D.passedTitle}</div>
                    <div className="wk-brain-box-body">{replay?.summary}</div>
                    {replay?.note && <div className="wk-brain-box-note">{replay.note}</div>}
                </div>
            )}
            {status === 'failed' && (
                <div className="wk-brain-box wk-brain-box--failed wk-brain-box--stack" data-testid="brain-detail-failed">
                    <div className="wk-brain-box-title">{D.failedTitle}</div>
                    <div className="wk-brain-box-body">{replay?.summary}</div>
                    {replay?.note && <div className="wk-brain-box-note">{replay.note}</div>}
                </div>
            )}
            {status === 'draft' && replay?.state === 'not_tested' && replay.summary && (
                <div className="wk-brain-box wk-brain-box--testing" data-testid="brain-detail-not-tested">
                    <WikiIcon name="info" size={14} />
                    <span>{replay.summary}</span>
                </div>
            )}

            {showLifecycle && (
                <div className="wk-brain-actions" data-testid="brain-detail-lifecycle">
                    {check && (status === 'draft' || status === 'failed') && (
                        <ActionButton variant="primary" icon="play" disabled={busy} onClick={() => onRunTest(node)} testId="brain-run-test">
                            {D.runTest}
                        </ActionButton>
                    )}
                    {check && status === 'tested' && canActivate && (
                        <ActionButton variant="primary" icon="check" disabled={busy} onClick={() => onSwitchOn(node)} testId="brain-switch-on">
                            {D.switchOn}
                        </ActionButton>
                    )}
                    {group && (status === 'draft' || status === 'off') && canActivate && (
                        <ActionButton variant="primary" icon="check" disabled={busy} onClick={() => onSwitchOn(node)} testId="brain-switch-group-on">
                            {D.switchGroupOn}
                        </ActionButton>
                    )}
                    <ActionButton icon="trash-2" disabled={busy} onClick={() => onDelete(node)} testId="brain-delete">
                        {D.delete}
                    </ActionButton>
                </div>
            )}

            {canManage && (
                <>
                    <div className="wk-t-label wk-brain-label">{D.buildLabel}</div>
                    <div className="wk-brain-actions wk-brain-actions--tight">
                        {canChild && (
                            <ActionButton variant="build" icon="plus" onClick={() => onAddChild(node.id)} testId="brain-add-child">
                                {brainAddChildLabel(node)}
                            </ActionButton>
                        )}
                        {canSibling && (
                            <ActionButton icon="git-merge" onClick={() => onBranchOut(node.id)} testId="brain-branch-out">
                                {brainBranchOutLabel(node)}
                            </ActionButton>
                        )}
                    </div>
                    <p className="wk-t-meta wk-brain-build-helper">{D.buildHelper}</p>
                </>
            )}

            {showFlagRow && (
                <div className="wk-brain-actions wk-brain-actions--flag" data-testid="brain-detail-flag-row">
                    <ActionButton icon="flag" disabled={busy} onClick={() => onFlag(node)} testId="brain-flag">
                        {D.flag}
                    </ActionButton>
                    {status === 'off' ? canActivate && (
                        <ActionButton icon="check" disabled={busy} onClick={() => onSwitchOn(node)} testId="brain-turn-on">
                            {D.switchOn}
                        </ActionButton>
                    ) : (
                        <ActionButton icon="pause" disabled={busy} onClick={() => onTurnOff(node)} testId="brain-turn-off">
                            {D.turnOff}
                        </ActionButton>
                    )}
                </div>
            )}
        </aside>
    );
}
